import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FormContentManager from "../../components/common/form/FormContentManager";
import TableCell from "../../components/common/ui/TableCell";
import * as designMeta from "../../api/design/meta";
import DesignTreePicker from "../../components/design/DesignTreePicker";
import axios from "../../api/axios";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";
import { getDocPersons } from "../../api/papers";

export default function Fcn_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const reasonEditorRef = useRef(null);

  const [requestPeople, setRequestPeople] = useState([]);

  const [form, setForm] = useState({
    title: "",
    reason: "",
    attachments: [],
    details: [{ id: 1, item: "", before: "", after: "", note: "" }],
  });

  /* ---------------------------------------------------
       수정 모드: 기존 meta 로드
    --------------------------------------------------- */
  useEffect(() => {
    if (!paramDns) return; // new 모드

    const load = async () => {
      try {
        const res = await designMeta.getMeta({ dns: paramDns });
        const meta = res.meta || res.data?.meta || {};

        const loadedPeople = await getDocPersons(paramDns);

        setDns(paramDns);

        setRequestPeople(loadedPeople);

        setForm({
          title: meta.title || "",
          requestDept: meta.requestDept || "",
          reason: meta.reason || "",
          attachments: meta.attachments || [],
          details: meta.details || [],
          alert: meta.alert || {
            send: true,
            urgent: false,
            departments: (loadedPeople || []).map((p) => p.id),
          },
        });
      } catch (e) {
        console.error(e);
        alert("기존 문서를 불러오지 못했습니다.");
      }
    };

    load();
  }, [paramDns]);

  const [dns, setDns] = useState("");
  const [linkedFcr, setLinkedFcr] = useState(null);
  const [fcrList, setFcrList] = useState([]);

  const getBody = useCallback(() => {
    const html = reasonEditorRef.current?.getContent() || "";

    return {
      values: {
        project: projectId,
        module: "design",
        doc_type: "FCN",
        dns,
        related_dns: linkedFcr?.doc_dns || null,

        title: form.title,

        meta: {
          title: form.title,
          reason: html || form.reason,
          requestPeople: requestPeople.map((p) => p.user?.id || p.id),

          details: form.details.map((d) => ({
            id: d.id,
            item: d.item,
            before: d.before,
            after: d.after,
            note: d.note,
            drawingFileId: d.drawingFileId || null,
            drawingFileUrl: d.drawingFileUrl || "",
          })),

          alert: {
            send: true,
            urgent: false,
            departments: requestPeople.map((p) => p.user?.id || p.id),
          },

          attachments: form.attachments || [],
          linkedFcr: linkedFcr?.doc_dns || null,
        },
      },
    };
  }, [form, dns, linkedFcr, projectId, requestPeople]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fcr_dns = params.get("fromFCR");
    if (fcr_dns) loadFcr(fcr_dns);
  }, [location.search, projectId]);

  useEffect(() => {
    const fetchFcrList = async () => {
      try {
        const res = await designMeta.getMetaMyList({
          project: projectId,
          doc_type: "FCR",
        });
        console.log("불러온 FCR 목록:", res);
        setFcrList(Array.isArray(res.results) ? res.results : []);
      } catch (err) {
        console.error("FCR 목록 불러오기 실패:", err);
      }
    };
    fetchFcrList();
  }, [projectId]);

  const loadFcr = async (fcr_dns) => {
    try {
      const res = await designMeta.getMeta({ dns: fcr_dns });
      const fcr = res.data || res;
      setLinkedFcr(fcr);

      const meta = fcr.meta || {};

      const loadedPeople = await getDocPersons(fcr_dns);
      setRequestPeople(loadedPeople);

      setForm((prev) => ({
        ...prev,

        title: meta.title
          ? `${meta.title} (설계변경 통보)`
          : prev.title || "설계변경 통보서",

        reason:
          prev.reason ||
          meta.reason ||
          "FCR 요청에 따라 설계 변경을 반영하고 통보합니다.",

        attachments: prev.attachments,

        details: (meta.details || prev.details).map((d, i) => ({
          id: i + 1,
          item: d.drawingNo || d.item || "",
          drawingFileId: d.drawingFileId || null,
          drawingFileUrl: d.drawingFileUrl || "",
          before: d.original || d.before || "",
          after: d.changed || d.after || "",
          note: d.cause || d.note || "",
        })),
      }));
    } catch (err) {
      console.error("FCR 불러오기 실패:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.details];
      updated[index][key] = value;
      return { ...prev, details: updated };
    });
  };

  const addDetailRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: prev.details.length + 1,
          item: "",
          before: "",
          after: "",
          note: "",
        },
      ],
    }));

  const removeDetailRow = (i) =>
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, idx) => idx !== i),
    }));

  return (
    <FormContentManager
      params={{
        module: "design",
        doc_type: "fcn",
        project: projectId,
        dns,
        related_dns: linkedFcr?.doc_dns || null,
      }}
      getBody={getBody}
      onChangeId={(newDns) => setDns(newDns)}
      redirectTo={`/project/${projectId}/design/list`}
      docName={"설계변경통보서 (FCN)"}
      docDescribe={"설계 변경 사항을 통보할 때 아래 내용을 작성하세요."}
      requestPeople={requestPeople}
      summaryConfig={{
        title: "통보서 요약",
        sections: [
          { label: "제목", value: form.title },
          {
            label: "통보 담당자",
            value:
              requestPeople.map((r) => r.user?.name || r.name).join(", ") ||
              "-",
          },
        ],
        list: [
          {
            label: "변경 항목",
            items: form.details?.map(
              (d) => `${d.item || "-"}: ${d.before || "-"} → ${d.after || "-"}`
            ),
          },
        ],
        footerNote: "입력한 내용이 실시간으로 반영됩니다.",
      }}
    >
      <div className="">
        <div className="mb-4 flex items-center gap-2">
          <label className="font-medium text-sm">연결된 FCR</label>
          {linkedFcr ? (
            <span className="text-sm">{linkedFcr.doc_dns}</span>
          ) : (
            <select
              className="border rounded px-2 py-1 text-sm"
              onChange={(e) => loadFcr(e.target.value)}
            >
              <option value="">선택하세요</option>
              {fcrList.map((fcr) => (
                <option key={fcr.doc_dns} value={fcr.doc_dns}>
                  {fcr.doc_dns} - {fcr.title}
                </option>
              ))}
            </select>
          )}
        </div>

        <table className="w-full border text-sm mb-6 table-fixed">
          <colgroup>
            <col style={{ width: "100px" }} />
            <col style={{ width: "70%" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "40%" }} />
          </colgroup>
          <tbody className="divide-y">
            <tr className="divide-x">
              <TableCell
                label="통보 제목"
                colSpan={3}
                input={
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="예: 벽체 두께 변경에 따른 구조 보강 통보"
                  />
                }
                editing
                labelClass="px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
              />
            </tr>
            <tr className="divide-x">
              <TableCell
                label="통보 담당자"
                colSpan={3}
                input={
                  <PeopleCellLoader
                    dns={linkedFcr?.doc_dns || dns}
                    value={requestPeople}
                    onChange={(list) => setRequestPeople(list)}
                  />
                }
                editing
                labelClass="px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
              />
            </tr>

            <tr className="divide-x">
              <TableCell
                label="변경 사유"
                input={
                  <TinyMCEeditor
                    height={300}
                    initialValue={form.reason}
                    editorRef={reasonEditorRef}
                    onChange={(v) =>
                      setForm((prev) => ({ ...prev, reason: v }))
                    }
                  />
                }
                editing
                colSpan={3}
                labelClass="px-3 py-1.5 align-top"
                valueClass="px-3 py-1.5 align-top"
              />
            </tr>
          </tbody>
        </table>

        <h2 className="text-body-bold mb-2">변경 항목 내역</h2>
        <div className="border overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead className="border-b bg-gray-100">
              <tr>
                <th className="px-3 py-2 border-r w-12 text-center font-medium">
                  No
                </th>
                <th className="px-3 py-2 border-r font-medium">항목</th>
                <th className="px-3 py-2 border-r font-medium">변경 전 내용</th>
                <th className="px-3 py-2 border-r font-medium">변경 후 내용</th>
                <th className="px-3 py-2 border-r font-medium">변경 사유</th>
                <th className="px-3 py-2 text-center w-14 font-medium">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {form.details.map((row, i) => (
                <tr key={row.id}>
                  <td className="px-3 py-1.5 text-center border-r">{i + 1}</td>
                  {["item", "before", "after", "note"].map((key) => (
                    <td key={key} className="px-3 py-1.5 border-r">
                      {key === "item" ? (
                        <div className="flex items-center gap-1">
                          {row.drawingFileId ? (
                            <div className="flex items-center justify-between w-full border rounded px-2 py-1 bg-slate-50">
                              <span className="truncate max-w-[150px]">
                                {row.item}
                              </span>
                              <button
                                onClick={() => {
                                  handleDetailChange(i, "drawingFileId", null);
                                  handleDetailChange(i, "drawingFileUrl", "");
                                  handleDetailChange(i, "item", "");
                                }}
                                className="ml-2 text-red-500 hover:text-red-600 text-xs"
                                title="선택 해제"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={row[key]}
                                onChange={(e) =>
                                  handleDetailChange(i, key, e.target.value)
                                }
                                className="w-full border rounded px-2 py-1 text-[13px]"
                                placeholder="도면번호 또는 선택"
                              />
                              <button
                                onClick={() => {
                                  setActiveRow(i);
                                  setShowDesignPicker(true);
                                }}
                                className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                                title="설계도서에서 선택"
                              >
                                📂
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={row[key]}
                          onChange={(e) =>
                            handleDetailChange(i, key, e.target.value)
                          }
                          className="w-full border rounded px-2 py-1 text-[13px]"
                        />
                      )}
                    </td>
                  ))}

                  <td className="px-3 py-1.5 text-center">
                    <button
                      onClick={() => removeDetailRow(i)}
                      className="text-xs underline"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-right mt-2">
          <button onClick={addDetailRow} className="btn-basic-sm">
            + 행 추가
          </button>
        </div>

        {linkedFcr && (
          <div className="mt-4 text-sm text-slate-600">
            ※ 본 문서는{" "}
            <a
              href={`/project/${projectId}/design/fcr/${linkedFcr.doc_dns}`}
              className="text-indigo-600 underline hover:text-indigo-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              FCR ({linkedFcr.doc_dns})
            </a>{" "}
            요청서를 기반으로 작성되었습니다.
          </div>
        )}
      </div>
      {showDesignPicker && (
        <DesignTreePicker
          open={showDesignPicker}
          onClose={() => setShowDesignPicker(false)}
          onConfirm={(file) => {
            handleDetailChange(activeRow, "item", file.name || file.title);
            handleDetailChange(activeRow, "drawingFileId", file.id);
            handleDetailChange(
              activeRow,
              "drawingFileUrl",
              file.file_url || file.url || ""
            );
          }}
        />
      )}
    </FormContentManager>
  );
}
